import React, { useEffect, useState, useRef } from 'react';

const SpaceParticles = () => {
  const [particles, setParticles] = useState([]);
  const [isVisible, setIsVisible] = useState(false);
  const particleContainerRef = useRef(null);
  const particleCountRef = useRef(0); // Use useRef to keep track of particle count
  const initialParticlesCreated = useRef(false); // Flag to check initial particles creation
  const initialParticles = 10;
  const newParticleInterval = 10000; // Keep the interval as 5 seconds
  const totalParticles = 15; // Maximum particle count

  useEffect(() => {
    // Function to create a new particle object
    const createParticle = (isInitial = false) => {
      if (particleCountRef.current >= totalParticles) return;

      const size = Math.random() * 30 + 5; // Random size between 5px and 35px
      const topPosition = Math.random() * (window.innerHeight - size); // Random vertical position
      const leftPosition = isInitial
        ? Math.random() * window.innerWidth // Random left position for initial particles
        : -50; // Start from the left side for new particles

      let duration = 0;
      if (size >= 5 && size <= 10) {
        duration = Math.random() * 700 + 20;
      } else if (size > 10 && size <= 15) {
        duration = Math.random() * 600 + 20;
      } else if (size > 15 && size <= 25) {
        duration = Math.random() * 500 + 20;
      } else {
        duration = Math.random() * 400 + 20;
      }

      const id = Date.now() + Math.random(); // Unique ID for each particle

      setParticles((prev) => [
        ...prev,
        { id, size, top: topPosition, left: leftPosition, duration },
      ]);

      particleCountRef.current++; // Increment the particle count

      // Remove particle after its animation duration
      setTimeout(() => {
        setParticles((prev) => prev.filter((p) => p.id !== id));
        particleCountRef.current--; // Decrement the particle count
      }, duration * 1000);
    };

    // Observer to detect if the container is visible
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.1 } // Trigger when at least 10% of the component is visible
    );

    if (particleContainerRef.current) {
      observer.observe(particleContainerRef.current);
    }

    // Generate initial particles only once
    if (isVisible && !initialParticlesCreated.current) {
      for (let i = 0; i < initialParticles; i++) {
        createParticle(true);
      }
      initialParticlesCreated.current = true; // Set the flag to true
    }

    // Generate new particles at intervals when visible
    const interval = setInterval(() => {
      if (isVisible) {
        createParticle();
      }
    }, newParticleInterval);

    return () => {
      clearInterval(interval);
      observer.disconnect();
    };
  }, [isVisible]);

  return (
    <div
      ref={particleContainerRef}
      style={{
        margin: 0,
        overflow: 'hidden',
        background: 'black',
        position: 'relative',
        height: '100vh',
        width: '100vw',
      }}
      className="space"
    >
      {particles.map((particle) => (
        <div
          key={particle.id}
          style={{
            position: 'absolute',
            backgroundImage: "url('/assets/images/one.png')",
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            borderRadius: '50%',
            opacity: 0.8,
            width: `${particle.size}px`,
            height: `${particle.size}px`,
            top: `${particle.top}px`,
            left: `${particle.left}px`,
            animation: `particleMove ${particle.duration}s linear infinite`,
          }}
        />
      ))}

      <style>{`
        @keyframes particleMove {
          from {
            transform: translateX(-50px);
          }
          to {
            transform: translateX(calc(100vw + 50px));
          }
        }
      `}</style>
    </div>
  );
};

export default SpaceParticles;
